// SwapWidget — buy $R₿L in-page via Uniswap V3 on Base L2.
// Self-contained: ships its own Wagmi/QueryClient providers so it does not
// touch any global provider or layout of the app.
import React, { useState, useEffect, useMemo } from "react";
import { WagmiProvider, useAccount, useConnect, useDisconnect, useSwitchChain, useReadContract, useWriteContract, useWaitForTransactionReceipt, useBalance } from "wagmi";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { parseUnits, formatUnits, encodePacked } from "viem";
import { ArrowDownUp, Wallet, ShieldCheck, Lock, ExternalLink, X, AlertTriangle, CheckCircle2 } from "lucide-react";
import { wagmiConfig, BASE_CHAIN_ID } from "../../web3/wagmiConfig";
import {
  WETH_BASE, USDC_BASE, RBL_BASE,
  UNISWAP_QUOTER_V2_BASE, UNISWAP_SWAP_ROUTER_02_BASE,
  FEE_RBL_WETH, FEE_USDC_WETH,
  erc20Abi, quoterV2Abi, swapRouter02Abi, applySlippage,
} from "../../web3/uniswap";
import { useLanguage } from "../../i18n/LanguageContext";

const UNISWAP_FALLBACK_URL = `https://app.uniswap.org/swap?outputCurrency=${RBL_BASE}&chain=base`;
const BASESCAN_TOKEN_URL = `https://basescan.org/token/${RBL_BASE}`;
const SLIPPAGE_BPS = 100; // 1%

const T = {
  es: {
    title: "Comprar $R₿L", powered: "Powered by Uniswap V3",
    wallet_none: "Wallet: no conectada", wallet_connected: "Conectada",
    connect: "Conectar Wallet", disconnect: "Desconectar",
    select_wallet: "Selecciona tu wallet", no_wallet: "¿No tienes wallet?",
    buy_on_uniswap: "Comprar en Uniswap", wrong_net: "Cambia a Base L2 para comprar $R₿L",
    switch_base: "Cambiar a Base", pay_with: "Pagas con", receive: "Recibes (estimado)",
    balance: "Balance", quoting: "Calculando precio…", min_received: "Mínimo (slippage 1%)",
    approve: "Aprobar USDC", approving: "Aprobando…", approved: "USDC aprobado",
    swap: "Comprar R₿L", swapping: "Comprando…", confirming: "Confirmando…",
    success: "¡Compra confirmada!", view_tx: "Ver transacción",
    verified: "Contrato verificado en Basescan", lp_locked: "LP bloqueado 12 meses · PinkLock",
    enter_amount: "Introduce una cantidad", no_liquidity: "Sin cotización disponible",
    injected: "MetaMask / Wallet del navegador",
  },
  en: {
    title: "Buy $R₿L", powered: "Powered by Uniswap V3",
    wallet_none: "Wallet: not connected", wallet_connected: "Connected",
    connect: "Connect Wallet", disconnect: "Disconnect",
    select_wallet: "Select your wallet", no_wallet: "No wallet?",
    buy_on_uniswap: "Buy on Uniswap", wrong_net: "Switch to Base L2 to buy $R₿L",
    switch_base: "Switch to Base", pay_with: "You pay", receive: "You receive (est.)",
    balance: "Balance", quoting: "Fetching quote…", min_received: "Minimum (1% slippage)",
    approve: "Approve USDC", approving: "Approving…", approved: "USDC approved",
    swap: "Buy R₿L", swapping: "Buying…", confirming: "Confirming…",
    success: "Purchase confirmed!", view_tx: "View transaction",
    verified: "Contract verified on Basescan", lp_locked: "LP locked 12 months · PinkLock",
    enter_amount: "Enter an amount", no_liquidity: "No quote available",
    injected: "MetaMask / Browser wallet",
  },
  sk: {
    title: "Kúpiť $R₿L", powered: "Powered by Uniswap V3",
    wallet_none: "Peňaženka: nepripojená", wallet_connected: "Pripojená",
    connect: "Pripojiť peňaženku", disconnect: "Odpojiť",
    select_wallet: "Vyber peňaženku", no_wallet: "Nemáš peňaženku?",
    buy_on_uniswap: "Kúpiť na Uniswap", wrong_net: "Prepni na Base L2 pre nákup $R₿L",
    switch_base: "Prepnúť na Base", pay_with: "Platíš", receive: "Dostaneš (odhad)",
    balance: "Zostatok", quoting: "Načítavam cenu…", min_received: "Minimum (1% slippage)",
    approve: "Schváliť USDC", approving: "Schvaľujem…", approved: "USDC schválené",
    swap: "Kúpiť R₿L", swapping: "Kupujem…", confirming: "Potvrdzujem…",
    success: "Nákup potvrdený!", view_tx: "Zobraziť transakciu",
    verified: "Kontrakt overený na Basescan", lp_locked: "LP uzamknuté 12 mesiacov · PinkLock",
    enter_amount: "Zadaj sumu", no_liquidity: "Cenová ponuka nedostupná",
    injected: "MetaMask / Peňaženka prehliadača",
  }, de: { title: "$R₿L kaufen", powered: "Powered by Uniswap V3", wallet_none: "Wallet: nicht verbunden", wallet_connected: "Verbunden", connect: "Wallet verbinden", disconnect: "Trennen", select_wallet: "Wähle deine Wallet", no_wallet: "Keine Wallet?", buy_on_uniswap: "Auf Uniswap kaufen", wrong_net: "Wechsle zu Base L2, um $R₿L zu kaufen", switch_base: "Zu Base wechseln", pay_with: "Du zahlst mit", receive: "Du erhältst (geschätzt)", balance: "Guthaben", quoting: "Preis wird berechnet…", min_received: "Minimum (Slippage 1%)", approve: "USDC genehmigen", approving: "Genehmigung läuft…", approved: "USDC genehmigt", swap: "R₿L kaufen", swapping: "Kauf läuft…", confirming: "Bestätigung läuft…", success: "Kauf bestätigt!", view_tx: "Transaktion ansehen", verified: "Vertrag verifiziert auf Basescan", lp_locked: "LP gesperrt 12 Monate · PinkLock", enter_amount: "Betrag eingeben", no_liquidity: "Keine Kursstellung verfügbar", injected: "MetaMask / Browser-Wallet" }, "zh-CN": { title: "购买 $R₿L", powered: "Powered by Uniswap V3", wallet_none: "Wallet: 未连接", wallet_connected: "已连接", connect: "连接 Wallet", disconnect: "断开连接", select_wallet: "选择你的 wallet", no_wallet: "没有 wallet？", buy_on_uniswap: "在 Uniswap 购买", wrong_net: "切换到 Base L2 以购买 $R₿L", switch_base: "切换到 Base", pay_with: "支付", receive: "接收（预估）", balance: "余额", quoting: "计算价格中…", min_received: "最低（滑点 1%）", approve: "授权 USDC", approving: "授权中…", approved: "USDC 已授权", swap: "购买 R₿L", swapping: "购买中…", confirming: "确认中…", success: "购买已确认！", view_tx: "查看交易", verified: "合约已在 Basescan 验证", lp_locked: "LP 锁定 12 个月 · PinkLock", enter_amount: "输入金额", no_liquidity: "无可用报价", injected: "MetaMask / 浏览器 Wallet" },
};

const CONNECTOR_LABELS = (t) => ({
  injected: t.injected,
  coinbaseWalletSDK: "Coinbase Wallet",
  walletConnect: "WalletConnect",
});

const USDC_PATH = encodePacked(
  ["address", "uint24", "address", "uint24", "address"],
  [USDC_BASE, FEE_USDC_WETH, WETH_BASE, FEE_RBL_WETH, RBL_BASE]
);

const fmtRbl = (v) =>
  Number(formatUnits(v, 18)).toLocaleString(undefined, { maximumFractionDigits: 0 });

function useDebounced(value, ms = 450) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

function ConnectModal({ t, onClose }) {
  const { connectors, connect, isPending, error } = useConnect();
  const labels = CONNECTOR_LABELS(t);
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/75 p-4" onClick={onClose} data-testid="swap-connect-modal">
      <div className="w-full max-w-xs rounded-2xl p-[1.5px]" style={{ background: "linear-gradient(135deg,#6d28d9,#f97316)" }} onClick={(e) => e.stopPropagation()}>
        <div className="rounded-2xl p-5" style={{ background: "#0a0a0f" }}>
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold uppercase tracking-[0.18em] text-white">{t.select_wallet}</h3>
            <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="swap-connect-modal-close"><X className="h-4 w-4" /></button>
          </div>
          <div className="mt-4 space-y-2">
            {connectors.map((c) => (
              <button
                key={c.uid}
                disabled={isPending}
                onClick={() => connect({ connector: c }, { onSuccess: onClose })}
                data-testid={`swap-connector-${c.id}`}
                className="flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left text-sm font-bold text-white transition-all hover:-translate-y-0.5 disabled:opacity-50"
                style={{ borderColor: "rgba(109,40,217,0.5)", background: "#111118" }}
              >
                <span>{labels[c.id] || c.name}</span>
                <Wallet className="h-4 w-4" style={{ color: "#f97316" }} />
              </button>
            ))}
          </div>
          {error && (
            <p className="mt-3 break-words text-[11px]" style={{ color: "#f87171" }} data-testid="swap-connect-error">
              {error.shortMessage || error.message}
            </p>
          )}
          <div className="mt-4 border-t border-white/10 pt-3 text-center">
            <p className="text-[11px]" style={{ color: "#9ca3af" }}>{t.no_wallet}</p>
            <a href={UNISWAP_FALLBACK_URL} target="_blank" rel="noopener noreferrer" data-testid="swap-fallback-uniswap-modal"
              className="mt-1 inline-flex items-center gap-1 text-xs font-bold" style={{ color: "#f97316" }}>
              {t.buy_on_uniswap} <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function SwapWidgetInner() {
  const { lang } = useLanguage();
  const t = T[lang] || T.en || T.es;

  const { address, isConnected, chainId: walletChainId } = useAccount();
  const { disconnect } = useDisconnect();
  const { switchChain, isPending: isSwitching } = useSwitchChain();

  const [showModal, setShowModal] = useState(false);
  const [inputToken, setInputToken] = useState("ETH"); // "ETH" | "USDC"
  const [amount, setAmount] = useState("");
  const debouncedAmount = useDebounced(amount);

  const wrongNetwork = isConnected && walletChainId !== BASE_CHAIN_ID;
  const isUsdc = inputToken === "USDC";
  const decimals = isUsdc ? 6 : 18;

  const amountIn = useMemo(() => {
    try {
      if (!debouncedAmount || Number(debouncedAmount) <= 0) return 0n;
      return parseUnits(debouncedAmount, decimals);
    } catch (_e) { return 0n; }
  }, [debouncedAmount, decimals]);

  // ── Quote (works without wallet) ──────────────────────────────────────────
  const { data: quoteData, isFetching: isQuoting } = useReadContract({
    address: UNISWAP_QUOTER_V2_BASE,
    abi: quoterV2Abi,
    functionName: isUsdc ? "quoteExactInput" : "quoteExactInputSingle",
    args: isUsdc
      ? [USDC_PATH, amountIn]
      : [{ tokenIn: WETH_BASE, tokenOut: RBL_BASE, amountIn, fee: FEE_RBL_WETH, sqrtPriceLimitX96: 0n }],
    chainId: BASE_CHAIN_ID,
    query: { enabled: amountIn > 0n, staleTime: 15_000, refetchInterval: 30_000 },
  });
  const amountOut = quoteData ? quoteData[0] : 0n;
  const amountOutMin = amountOut > 0n ? applySlippage(amountOut, SLIPPAGE_BPS) : 0n;

  // ── Balances ──────────────────────────────────────────────────────────────
  const { data: ethBalance } = useBalance({
    address, chainId: BASE_CHAIN_ID,
    query: { enabled: isConnected && !isUsdc },
  });
  const { data: usdcBalance } = useReadContract({
    address: USDC_BASE, abi: erc20Abi, functionName: "balanceOf",
    args: [address], chainId: BASE_CHAIN_ID,
    query: { enabled: isConnected && isUsdc && !!address },
  });

  // ── USDC allowance ────────────────────────────────────────────────────────
  const { data: allowance, refetch: refetchAllowance } = useReadContract({
    address: USDC_BASE, abi: erc20Abi, functionName: "allowance",
    args: [address, UNISWAP_SWAP_ROUTER_02_BASE], chainId: BASE_CHAIN_ID,
    query: { enabled: isConnected && isUsdc && !!address },
  });
  const needsApproval = isUsdc && amountIn > 0n && (allowance ?? 0n) < amountIn;

  // ── Approve tx ────────────────────────────────────────────────────────────
  const { data: approveHash, isPending: isApproving, writeContract: writeApprove, error: approveError, reset: resetApprove } = useWriteContract();
  const { isLoading: approveWaiting, isSuccess: approveSuccess } = useWaitForTransactionReceipt({ hash: approveHash, chainId: BASE_CHAIN_ID });
  useEffect(() => { if (approveSuccess) refetchAllowance(); }, [approveSuccess, refetchAllowance]);

  // ── Swap tx ───────────────────────────────────────────────────────────────
  const { data: swapHash, isPending: isSwapping, writeContract: writeSwap, error: swapError, reset: resetSwap } = useWriteContract();
  const { isLoading: swapWaiting, isSuccess: swapSuccess } = useWaitForTransactionReceipt({ hash: swapHash, chainId: BASE_CHAIN_ID });
  useEffect(() => { if (swapSuccess) setAmount(""); }, [swapSuccess]);

  // Reset tx state when switching token/amount source
  useEffect(() => { resetSwap(); resetApprove(); }, [inputToken, resetSwap, resetApprove]);

  const onApprove = () => {
    writeApprove({
      address: USDC_BASE, abi: erc20Abi, functionName: "approve",
      chainId: BASE_CHAIN_ID,
      args: [UNISWAP_SWAP_ROUTER_02_BASE, amountIn],
    });
  };

  const onSwap = () => {
    if (!isConnected || wrongNetwork || amountIn <= 0n || amountOutMin <= 0n) return;
    if (isUsdc) {
      writeSwap({
        address: UNISWAP_SWAP_ROUTER_02_BASE, abi: swapRouter02Abi, functionName: "exactInput",
        chainId: BASE_CHAIN_ID,
        args: [{ path: USDC_PATH, recipient: address, amountIn, amountOutMinimum: amountOutMin }],
      });
    } else {
      writeSwap({
        address: UNISWAP_SWAP_ROUTER_02_BASE, abi: swapRouter02Abi, functionName: "exactInputSingle",
        chainId: BASE_CHAIN_ID,
        value: amountIn, // native ETH — router wraps to WETH
        args: [{
          tokenIn: WETH_BASE, tokenOut: RBL_BASE, fee: FEE_RBL_WETH,
          recipient: address, amountIn, amountOutMinimum: amountOutMin, sqrtPriceLimitX96: 0n,
        }],
      });
    }
  };

  const busy = isApproving || approveWaiting || isSwapping || swapWaiting;
  const balanceDisplay = isConnected
    ? isUsdc
      ? usdcBalance != null ? `${Number(formatUnits(usdcBalance, 6)).toLocaleString(undefined, { maximumFractionDigits: 2 })} USDC` : "…"
      : ethBalance ? `${Number(ethBalance.formatted).toLocaleString(undefined, { maximumFractionDigits: 5 })} ETH` : "…"
    : null;

  const tokenBtn = (sym) => (
    <button
      key={sym}
      onClick={() => setInputToken(sym)}
      data-testid={`swap-token-${sym.toLowerCase()}`}
      className="rounded-full px-4 py-1.5 text-xs font-black uppercase tracking-wider transition-all"
      style={inputToken === sym
        ? { background: "#f97316", color: "#0a0a0f" }
        : { background: "#1a1a2e", color: "#9ca3af", border: "1px solid rgba(109,40,217,0.4)" }}
    >
      {sym}
    </button>
  );

  return (
    <div className="rounded-2xl p-[1.5px]" style={{ background: "linear-gradient(135deg,#6d28d9,#f97316)" }} data-testid="swap-widget">
      <div className="rounded-2xl p-5" style={{ background: "#0a0a0f" }}>
        {/* Header */}
        <div className="flex items-center gap-2">
          <ArrowDownUp className="h-5 w-5" style={{ color: "#f97316" }} />
          <h2 className="text-base font-black uppercase tracking-[0.14em] text-white">{t.title}</h2>
        </div>
        <p className="mt-0.5 text-[10px] font-mono uppercase tracking-[0.2em]" style={{ color: "#9ca3af" }}>{t.powered}</p>

        {/* Wallet state */}
        <div className="mt-4 flex items-center justify-between rounded-xl px-3 py-2.5" style={{ background: "#111118" }}>
          {isConnected ? (
            <>
              <span className="font-mono text-xs text-white" data-testid="swap-wallet-address">
                <span className="mr-1.5 inline-block h-2 w-2 rounded-full" style={{ background: "#22c55e" }} />
                {address?.slice(0, 6)}…{address?.slice(-4)}
              </span>
              <button onClick={() => disconnect()} data-testid="swap-disconnect-btn"
                className="text-[11px] font-bold uppercase tracking-wider hover:text-white" style={{ color: "#9ca3af" }}>
                {t.disconnect}
              </button>
            </>
          ) : (
            <>
              <span className="text-xs" style={{ color: "#9ca3af" }} data-testid="swap-wallet-status">{t.wallet_none}</span>
              <button onClick={() => setShowModal(true)} data-testid="swap-connect-btn"
                className="rounded-full px-4 py-1.5 text-xs font-black uppercase tracking-wider transition-all hover:-translate-y-0.5"
                style={{ background: "#f97316", color: "#0a0a0f" }}>
                {t.connect}
              </button>
            </>
          )}
        </div>

        {/* Wrong network banner */}
        {wrongNetwork && (
          <div className="mt-3 flex items-center justify-between gap-2 rounded-xl border px-3 py-2.5"
            style={{ borderColor: "rgba(249,115,22,0.5)", background: "rgba(249,115,22,0.08)" }} data-testid="swap-wrong-network">
            <span className="flex items-center gap-1.5 text-[11px] font-bold" style={{ color: "#f97316" }}>
              <AlertTriangle className="h-3.5 w-3.5" /> {t.wrong_net}
            </span>
            <button onClick={() => switchChain({ chainId: BASE_CHAIN_ID })} disabled={isSwitching} data-testid="swap-switch-chain-btn"
              className="shrink-0 rounded-full px-3 py-1 text-[11px] font-black uppercase disabled:opacity-50"
              style={{ background: "#f97316", color: "#0a0a0f" }}>
              {t.switch_base}
            </button>
          </div>
        )}

        {/* Pay with */}
        <div className="mt-4 rounded-xl p-3" style={{ background: "#111118" }}>
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-mono uppercase tracking-[0.18em]" style={{ color: "#9ca3af" }}>{t.pay_with}</span>
            <div className="flex gap-1.5">{tokenBtn("ETH")}{tokenBtn("USDC")}</div>
          </div>
          <input
            type="number" min="0" step="any" inputMode="decimal" placeholder="0.0"
            value={amount} onChange={(e) => setAmount(e.target.value)}
            data-testid="swap-amount-input"
            className="mt-2 w-full bg-transparent font-mono text-2xl font-bold text-white outline-none placeholder:text-white/25"
          />
          {balanceDisplay && (
            <p className="mt-1 text-[10px] font-mono" style={{ color: "#9ca3af" }} data-testid="swap-balance">
              {t.balance}: {balanceDisplay}
            </p>
          )}
        </div>

        {/* Receive */}
        <div className="mt-2 rounded-xl p-3" style={{ background: "#111118" }}>
          <span className="text-[10px] font-mono uppercase tracking-[0.18em]" style={{ color: "#9ca3af" }}>{t.receive}</span>
          <p className="mt-1 font-mono text-2xl font-bold" style={{ color: "#f97316" }} data-testid="swap-quote-output">
            {isQuoting ? <span className="text-sm text-white/40">{t.quoting}</span>
              : amountOut > 0n ? <>{fmtRbl(amountOut)} <span className="text-sm">R₿L</span></>
              : amountIn > 0n && !isQuoting ? <span className="text-sm text-white/40">{t.no_liquidity}</span>
              : <span className="text-white/25">0</span>}
          </p>
          {amountOutMin > 0n && (
            <p className="mt-1 text-[10px] font-mono" style={{ color: "#9ca3af" }} data-testid="swap-min-received">
              {t.min_received}: {fmtRbl(amountOutMin)} R₿L
            </p>
          )}
        </div>

        {/* Actions */}
        {isConnected ? (
          <div className="mt-4 space-y-2">
            {needsApproval && (
              <button onClick={onApprove} disabled={busy || wrongNetwork} data-testid="swap-approve-btn"
                className="w-full rounded-xl border py-3 text-sm font-black uppercase tracking-[0.14em] text-white transition-all hover:-translate-y-0.5 disabled:opacity-40"
                style={{ borderColor: "#6d28d9", background: "rgba(109,40,217,0.15)" }}>
                {isApproving || approveWaiting ? t.approving : t.approve}
              </button>
            )}
            <button onClick={onSwap}
              disabled={busy || wrongNetwork || needsApproval || amountIn <= 0n || amountOutMin <= 0n}
              data-testid="swap-execute-btn"
              className="w-full rounded-xl py-3 text-sm font-black uppercase tracking-[0.14em] transition-all hover:-translate-y-0.5 disabled:opacity-40"
              style={{ background: "linear-gradient(135deg,#f97316,#6d28d9)", color: "#fff" }}>
              {isSwapping ? t.swapping : swapWaiting ? t.confirming : t.swap}
            </button>
          </div>
        ) : (
          <a href={UNISWAP_FALLBACK_URL} target="_blank" rel="noopener noreferrer" data-testid="swap-fallback-uniswap"
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border py-3 text-sm font-black uppercase tracking-[0.14em] text-white transition-all hover:-translate-y-0.5"
            style={{ borderColor: "#f97316", background: "rgba(249,115,22,0.08)" }}>
            {t.buy_on_uniswap} <ExternalLink className="h-4 w-4" style={{ color: "#f97316" }} />
          </a>
        )}

        {/* Tx feedback */}
        {approveSuccess && !needsApproval && !swapHash && (
          <p className="mt-2 flex items-center gap-1.5 text-[11px] font-bold" style={{ color: "#22c55e" }}>
            <CheckCircle2 className="h-3.5 w-3.5" /> {t.approved}
          </p>
        )}
        {swapSuccess && swapHash && (
          <p className="mt-2 flex items-center gap-1.5 text-[11px] font-bold" style={{ color: "#22c55e" }} data-testid="swap-success">
            <CheckCircle2 className="h-3.5 w-3.5" /> {t.success}{" "}
            <a href={`https://basescan.org/tx/${swapHash}`} target="_blank" rel="noopener noreferrer" className="underline">
              {t.view_tx}
            </a>
          </p>
        )}
        {(swapError || approveError) && (
          <p className="mt-2 break-words text-[11px]" style={{ color: "#f87171" }} data-testid="swap-error">
            {(swapError || approveError).shortMessage || (swapError || approveError).message}
          </p>
        )}

        {/* Trust footer */}
        <div className="mt-4 space-y-1.5 border-t border-white/10 pt-3">
          <a href={BASESCAN_TOKEN_URL} target="_blank" rel="noopener noreferrer" data-testid="swap-basescan-link"
            className="flex items-center gap-1.5 text-[11px] hover:text-white" style={{ color: "#9ca3af" }}>
            <ShieldCheck className="h-3.5 w-3.5" style={{ color: "#22c55e" }} /> {t.verified}
          </a>
          <p className="flex items-center gap-1.5 text-[11px]" style={{ color: "#9ca3af" }} data-testid="swap-lp-locked">
            <Lock className="h-3.5 w-3.5" style={{ color: "#22c55e" }} /> {t.lp_locked}
          </p>
        </div>
      </div>

      {showModal && <ConnectModal t={t} onClose={() => setShowModal(false)} />}
    </div>
  );
}

const queryClient = new QueryClient();

export default function SwapWidget() {
  return (
    <WagmiProvider config={wagmiConfig}>
      <QueryClientProvider client={queryClient}>
        <SwapWidgetInner />
      </QueryClientProvider>
    </WagmiProvider>
  );
}
